import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, BuildRounded, Person } from "@mui/icons-material";
import { ACEcolors, Icon, IconType, Input, InputType, MuiButton, MuiCard } from "@ace/ace-ui-web";
import { CardContent, Dialog, DialogContent, DialogTitle, FormControlLabel, Link, Radio, RadioGroup, Typography, Alert } from "@mui/material"
import { ReactComponent as AceLogo } from '../../assets/Ace_Hardware_Logo.svg';
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { loginUser } from '../../redux/AuthReducer/authSlice';
import { forgotPassword } from '../../redux/PasswordResetReducer/passwordResetSlice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handlePhoneKeyDown } from '../../utils/phoneUtils';

// Stylesheets
import styles from "./login.styles.module.css";

// Email validation regex (RFC 5322 compliant)
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const LoginPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { forgotLoading } = useAppSelector((state) => state.passwordReset);
    const [searchParams] = useSearchParams();
    const { loading } = useAppSelector((state) => state.login);


    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Forgot Password Modal State
    const [showForgotPassword, setShowForgotPassword] = useState(false)
    const [forgotPasswordWorkdayId, setForgotPasswordWorkdayId] = useState("")
    const [forgotPasswordMethod, setForgotPasswordMethod] = useState<"email" | "sms">("email")
    const [forgotPasswordInput, setForgotPasswordInput] = useState("")
    const [forgotPasswordError, setForgotPasswordError] = useState("")
    const [forgotPasswordWorkdayIdError, setForgotPasswordWorkdayIdError] = useState("")

    // Refs
    const forgotPhoneContainerRef = useRef<HTMLDivElement>(null);

    // Get returnUrl from query params
    const returnUrl = searchParams.get('returnUrl');
    const hasReturnUrl = !!returnUrl;

    // Auto-focus email input on mount
    useEffect(() => {
        const emailInput = document.querySelector('input[type="email"]') as HTMLInputElement;
        if (emailInput) {
            emailInput.focus();
        }
    }, []);

    useEffect(() => {
        if (forgotPasswordMethod !== "sms" || !forgotPhoneContainerRef.current) return;
        const input = forgotPhoneContainerRef.current.querySelector("input");
        if (!input) return;
        input.addEventListener("keydown", handlePhoneKeyDown);
        return () => {
            input.removeEventListener("keydown", handlePhoneKeyDown);
        };
    }, [forgotPasswordMethod]);

    const validateInput = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!email.trim()) {
            newErrors.email = "Email is required";
        } else if (!EMAIL_REGEX.test(email)) {
            newErrors.email = "Please enter a valid email";
        }

        if (!password.trim()) {
            newErrors.password = "Please enter your password";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSignIn = async () => {
        if (!validateInput()) {
            toast.error("Please complete all required fields");
            return;
        }

        try {
            const result = await dispatch(loginUser({ email, password })).unwrap();
            toast.success("Login successful!");

            // Super users must select a site before using the app
            if (result?.isSuperUser) {
                navigate('/site-select', { replace: true });
                return;
            }

            // Redirect to returnUrl if present, otherwise go to home
            const destination = returnUrl || '/';
            navigate(destination, { replace: true });
        } catch (error: any) {
            const errorMessage = error?.isPending
                ? "Login Failed - " + error.message
                : "Login failed. Please check your credentials.";
            toast.error(errorMessage, { duration: 6000 });
        }
    };

    const handleForgotPasswordMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setForgotPasswordMethod(event.target.value as "email" | "sms")
        setForgotPasswordInput("")
        setForgotPasswordError("")
    }

    const handleForgotPasswordClose = () => {
        setShowForgotPassword(false)
        setForgotPasswordWorkdayId("")
        setForgotPasswordMethod("email")
        setForgotPasswordInput("")
        setForgotPasswordError("")
        setForgotPasswordWorkdayIdError("")
    }

    const handleWorkdayIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = e.target.value.replaceAll(/\D/g, "")
        
        // Hard stop at 9 characters
        const limitedValue = numericValue.slice(0, 9)
        setForgotPasswordWorkdayId(limitedValue)
        
        // Clear error when user starts typing
        if (forgotPasswordWorkdayIdError) {
            setForgotPasswordWorkdayIdError("")
        }
        
        // Show validation message if user tries to enter more than 9 digits
        if (numericValue.length > 9) {
            setForgotPasswordWorkdayIdError("Workday ID cannot exceed 9 digits")
        }
    }

    const validateForgotPasswordInput = (): boolean => {
        let isValid = true

        // Validate Workday ID
        if (!forgotPasswordWorkdayId.trim()) {
            setForgotPasswordWorkdayIdError("Workday ID is required")
            isValid = false
        } else if (forgotPasswordWorkdayId.length < 3) {
            setForgotPasswordWorkdayIdError("Workday ID must be at least 4 digits")
            isValid = false
        }

        // Validate email/phone based on method
        const isEmail = forgotPasswordMethod === "email"

        const isEmpty = !forgotPasswordInput.trim()
        const isInvalidEmail = isEmail && !EMAIL_REGEX.test(forgotPasswordInput)
        const isInvalidPhone = !isEmail && forgotPasswordInput.replaceAll(/\D/g, "").length < 10

        if (isEmpty) {
            setForgotPasswordError(isEmail ? "Email is required" : "Phone number is required")
            isValid = false
        } else if (isInvalidEmail) {
            setForgotPasswordError("Please enter a valid email")
            isValid = false
        } else if (isInvalidPhone) {
            setForgotPasswordError("Please enter a complete 10-digit phone number")
            isValid = false
        }

        return isValid
    }

    const handleSendResetLink = async () => {
        if (!validateForgotPasswordInput()) return

        try {
            const payload = forgotPasswordMethod === "email"
                ? { workdayId: forgotPasswordWorkdayId, email: forgotPasswordInput }
                : { workdayId: forgotPasswordWorkdayId, phoneNumber: forgotPasswordInput }

            await dispatch(forgotPassword(payload)).unwrap()

            // Backend always returns 200 regardless of whether the account exists
            toast.success(
                forgotPasswordMethod === "email"
                    ? "If an account exists for this email, a reset link has been sent."
                    : "If an account exists for this number, a reset code has been sent via SMS."
            )
            handleForgotPasswordClose()
        } catch (error: any) {
            toast.error(error || "An error occurred. Please try again.")
        }
    }

    const handleForgotPasswordPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = e.target.value.replaceAll(/\D/g, "").slice(0, 10)
        let formatted = numericValue
        if (numericValue.length > 6) {
            formatted = `${numericValue.slice(0, 3)}-${numericValue.slice(3, 6)}-${numericValue.slice(6)}`
        } else if (numericValue.length > 3) {
            formatted = `${numericValue.slice(0, 3)}-${numericValue.slice(3)}`
        }
        setForgotPasswordInput(formatted)
        if (forgotPasswordError) setForgotPasswordError("")
    }

    // Handle form submission
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        handleSignIn();
    };

    return (
        <div className={styles.appContainer}>
            <div className={styles.contentWrapper}>
                <motion.div
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className={styles.header}
                >
                    <div className={styles.logoContainer}>
                        <AceLogo style={{ width: "100%", height: "100%" }} />
                    </div>
                    <div className={styles.headerBackground}>
                        <BuildRounded fontSize="small" sx={{ transform: 'scaleX(-1)', color: ACEcolors.primary.default }} />
                        <p className={styles.header2}>
                            Pickup • Swap • VTO
                        </p>
                    </div>
                    <h1 className={styles.title}>
                        Welcome Back!
                    </h1>
                </motion.div>

                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    <MuiCard
                        borderRadius={2}
                        elevation={5}
                        height="auto"
                        width="auto"
                        padding={1}
                        variant="elevation"
                    >
                        <div>
                            <Typography variant="h6" component="h2" sx={{ textAlign: 'center', fontSize: '26px', fontWeight: 'bold' }}>
                                Sign In
                            </Typography>
                        </div>
                        <CardContent>
                            {/* Show info alert when returnUrl is present */}
                            {hasReturnUrl && (
                                <Alert
                                    severity="info"
                                    sx={{ mb: 2 }}
                                >
                                    Please log in to continue
                                </Alert>
                            )}

                            <form className={styles.formCredentials} onSubmit={handleSubmit}>
                                {/* Email */}
                                <div className={[styles.formGroup, 'form-group'].join(" ")}>
                                    <div className={`${styles.formGrid} ${styles.formGridIconCols}`}>
                                        <Icon iconType={IconType.envelope} style={{ height: 15, width: 12 }} color="gray" />
                                        <p className={styles.formLabel}>Email</p>
                                    </div>
                                    <Input
                                        type={InputType.Email}
                                        placeholder="john.doe@example.com"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (errors.email) setErrors(prev => ({ ...prev, email: "" }));
                                        }}
                                    />
                                    {errors.email && (
                                        <p className={styles.errorMessage}>{errors.email}</p>
                                    )}
                                </div>

                                {/* Password */}
                                <div className={[styles.formGroup, 'form-group'].join(" ")}>
                                    <div className={`${styles.formGrid} ${styles.formGridIconCols}`}>
                                        <Lock sx={{ height: 1, fontSize: 15, color: "gray" }} />
                                        <p className={styles.formLabel}>Password</p>
                                    </div>
                                    <Input
                                        type={InputType.Password}
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            if (errors.password) setErrors(prev => ({ ...prev, password: "" }));
                                        }}
                                    />
                                    {errors.password && (
                                        <p className={styles.errorMessage}>{errors.password}</p>
                                    )}
                                    <Link
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        onClick={() => setShowForgotPassword(true)}
                                        sx={{
                                            alignSelf: 'flex-end',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            color: ACEcolors.primary.default,
                                            fontFamily: 'var(--font-body)',
                                            mt: -0.5,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Forgot Password?
                                    </Link>
                                </div>

                                <div className={styles.buttonGroup}>
                                    <motion.div
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        className={styles.buttonFull}
                                    >
                                        <MuiButton
                                            size="large"
                                            disabled={loading}
                                            onClick={handleSignIn}
                                            sx={{ width: '100%' }}
                                        >
                                            {loading ? (
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                    className={styles.spinner}
                                                />
                                            ) : (
                                                "Sign In"
                                            )}
                                        </MuiButton>
                                    </motion.div>
                                </div>
                            </form>
                        </CardContent>
                    </MuiCard>
                </motion.div>

                <p className={`${styles.footerText} ${styles.footerRegister}`}>
                    {"Don't have an account? "}
                    <Link href="/registration" underline="hover" color={ACEcolors.primary.default} sx={{ fontWeight: 'bold' }}>
                        Register here
                    </Link>
                </p>
            </div>

            {/* Forgot Password Modal */}
            <Dialog
                open={showForgotPassword}
                onClose={handleForgotPasswordClose}
                slotProps={{ paper: { sx: { borderRadius: 2, width: '100%', maxWidth: '26rem' } } }}
            >
                <DialogContent sx={{ p: 0 }}>
                    <DialogTitle sx={{
                        fontFamily: 'var(--font-heading)',
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        pb: 0.5,
                    }}>
                        Reset Your Password
                    </DialogTitle>
                    <div style={{ padding: '0 1.5rem 1.5rem' }}>
                        <Typography
                            variant="body2"
                            sx={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-body)', mb: 2 }}
                        >
                            Enter your Workday ID and choose how you&#39;d like to receive your reset link.
                        </Typography>

                        {/* Workday ID Input */}
                        <div className={[styles.formGroup, 'form-group'].join(" ")} style={{ marginBottom: '1.25rem' }}>
                            <div className={`${styles.formGrid} ${styles.formGridIconCols}`}>
                                <Person sx={{ height: 1, fontSize: 15, color: "gray" }} />
                                <p className={styles.formLabel}>Workday ID</p>
                            </div>
                            <Input
                                type={InputType.Number}
                                placeholder="Enter your Workday ID"
                                value={forgotPasswordWorkdayId}
                                onChange={handleWorkdayIdChange}
                                maxlength={9}
                            />
                            {forgotPasswordWorkdayIdError && (
                                <p className={styles.errorMessage}>{forgotPasswordWorkdayIdError}</p>
                            )}
                        </div>

                        {/* Radio Buttons */}
                        <RadioGroup
                            row
                            value={forgotPasswordMethod}
                            onChange={handleForgotPasswordMethodChange}
                            sx={{ mb: 2, gap: 2 }}
                        >
                            <FormControlLabel
                                value="email"
                                control={<Radio size="small" sx={{ color: ACEcolors.primary.default, '&.Mui-checked': { color: ACEcolors.primary.default } }} />}
                                label={<Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', fontWeight: 500 }}>Email</Typography>}
                            />
                            <FormControlLabel
                                value="sms"
                                control={<Radio size="small" sx={{ color: ACEcolors.primary.default, '&.Mui-checked': { color: ACEcolors.primary.default } }} />}
                                label={<Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', fontWeight: 500 }}>SMS</Typography>}
                            />
                        </RadioGroup>

                        {/* Dynamic Input */}
                        <div
                            ref={forgotPhoneContainerRef}
                            className={[styles.formGroup, 'form-group'].join(" ")}
                            style={{ marginBottom: '1.25rem' }}
                        >
                            <div className={`${styles.formGrid} ${styles.formGridIconCols}`}>
                                <Icon
                                    iconType={forgotPasswordMethod === "email" ? IconType.envelope : IconType.phone}
                                    style={{ height: 15, width: 12 }}
                                    color="gray"
                                />
                                <p className={styles.formLabel}>
                                    {forgotPasswordMethod === "email" ? "Email" : "Phone Number"}
                                </p>
                            </div>
                            <Input
                                type={forgotPasswordMethod === "email" ? InputType.Email : InputType.Phone}
                                placeholder={forgotPasswordMethod === "email" ? "john.doe@example.com" : "555-123-4567"}
                                value={forgotPasswordInput}
                                onChange={forgotPasswordMethod === "email"
                                    ? (e) => {
                                        setForgotPasswordInput(e.target.value)
                                        if (forgotPasswordError) setForgotPasswordError("")
                                    }
                                    : handleForgotPasswordPhoneChange
                                }
                            />
                            {forgotPasswordError && (
                                <p className={styles.errorMessage}>{forgotPasswordError}</p>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className={styles.buttonGroup} style={{ justifyContent: 'flex-end' }}>
                            <motion.div
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                            >
                                <MuiButton
                                    variant="outlined"
                                    size="medium"
                                    onClick={handleForgotPasswordClose}
                                >
                                    Cancel
                                </MuiButton>
                            </motion.div>
                            <motion.div
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                            >
                                <MuiButton
                                    size="medium"
                                    disabled={forgotLoading}
                                    onClick={handleSendResetLink}
                                >
                                    {forgotLoading ? (
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                            className={styles.spinner}
                                        />
                                    ) : (
                                        "Send Reset Link"
                                    )}
                                </MuiButton>
                            </motion.div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default LoginPage;